import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  VStack,
  SimpleGrid,
  Flex,
  Button,
  ButtonGroup,
  Icon,
} from '@chakra-ui/react';
import { FiLogIn } from 'react-icons/fi';
import { MaskedInput } from '../../../../../../components/Form/MaskedInput';
import { Switch } from '../../../../../../components/Form/Switch';

export type UpdateAppFormData = {
  description?: string;
  hookUrl?: string;
  name: string;
  ref: string;
  storeId?: string;
  usePagarmeProvider: boolean;
};
interface IAppProps {
  app: UpdateAppFormData;
  onSubmit: (appData: UpdateAppFormData) => void;
}

const appRegisterFormSchema = Yup.object().shape({
  description: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  name: Yup.string().required('Requerido'),
  ref: Yup.string().required('Requerido'),
  storeId: Yup.string()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
  usePagarmeProvider: Yup.boolean().default(true),
  hookUrl: Yup.string()
    .url('Inválido')
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value)),
});

export const UpdateAppForm = ({ app, onSubmit }: IAppProps): JSX.Element => {
  const { goBack } = useHistory();

  const { register, handleSubmit, formState, reset, watch } = useForm({
    resolver: yupResolver(appRegisterFormSchema),
  });

  const { errors } = formState;

  const usePagarmeProvider: boolean = watch('usePagarmeProvider');

  useEffect(() => {
    reset({
      description: app.description,
      hookUrl: app.hookUrl,
      name: app.name,
      ref: app.ref,
      storeId: app.storeId,
      usePagarmeProvider: app.usePagarmeProvider,
    });
  }, [app, reset]);

  const handleNewApp: SubmitHandler<UpdateAppFormData> = useCallback(
    (appData) => {
      onSubmit(appData);
    },
    [onSubmit],
  );

  return (
    <Box as="form" onSubmit={handleSubmit(handleNewApp)}>
      <VStack spacing="8">
        <MaskedInput label="Nome" error={errors.name} {...register('name')} />

        <SimpleGrid minChildWidth="240px" spacing="8" w="100%">
          <MaskedInput
            label="Referência"
            autoComplete="off"
            textTransform="uppercase"
            error={errors.ref}
            {...register('ref')}
          />

          <MaskedInput
            label="API Hook Url"
            autoComplete="off"
            type="hookUrl"
            error={errors.hookUrl}
            {...register('hookUrl')}
          />

          <MaskedInput
            label="Store id"
            error={errors.storeId}
            {...register('storeId')}
          />
        </SimpleGrid>

        <MaskedInput
          as="textarea"
          minHeight="100px"
          resize="none"
          py="2"
          label="Descrição"
          error={errors.description}
          {...register('description')}
        />

        <Flex>
          <Switch
            label="Use Pagar.me"
            error={errors.usePagarmeProvider}
            direction="row"
            {...register('usePagarmeProvider')}
          />
        </Flex>
      </VStack>

      <Flex mt="12" justify="flex-end">
        <ButtonGroup>
          <Button colorScheme="blackAlpha" onClick={goBack}>
            Cancelar
          </Button>
          <Button
            type="submit"
            colorScheme="green"
            isLoading={formState.isSubmitting}
          >
            {usePagarmeProvider ? 'Editar recebedor' : 'Salvar'}
            <Icon as={FiLogIn} fontSize="20" ml="2" />
          </Button>
        </ButtonGroup>
      </Flex>
    </Box>
  );
};
